import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Typography, Container, Box, Divider } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BreadcrumbsNav from "../components/BreadcrumbsNav";
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';

interface BlogPostMeta {
    slug: string;
    title: string;
    date: string;
    summary: string;
    file: string;
}

// Render a Mermaid diagram. Mermaid is dynamically imported so it stays out of
// the main bundle and only loads on blog posts that contain a diagram.
const Mermaid: React.FC<{ chart: string }> = ({ chart }) => {
    const [svg, setSvg] = useState<string>("");
    const [error, setError] = useState<boolean>(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const mermaid = (await import("mermaid")).default;
                mermaid.initialize({
                    startOnLoad: false,
                    theme: "base",
                    securityLevel: "strict",
                    fontFamily: "'DM Sans', sans-serif",
                    themeVariables: {
                        background: "#0d1016",
                        primaryColor: "#161a21",
                        primaryBorderColor: "#0077ff",
                        primaryTextColor: "#f0f2f5",
                        secondaryColor: "#1d232c",
                        tertiaryColor: "#13171d",
                        lineColor: "#5b6470",
                        textColor: "#c9d1d9",
                        fontSize: "13px",
                    },
                });
                const id = "mmd-" + Math.random().toString(36).slice(2);
                const { svg } = await mermaid.render(id, chart);
                if (!cancelled) setSvg(svg);
            } catch (e) {
                if (!cancelled) setError(true);
            }
        })();
        return () => { cancelled = true; };
    }, [chart]);

    if (error) {
        return (
            <Box component="pre" sx={{ bgcolor: "#0d1016", border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, mb: 2.5, overflowX: "auto", fontSize: "12.5px" }}>
                {chart}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                my: 3,
                p: 2,
                bgcolor: "#0d1016",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflowX: "auto",
                textAlign: "center",
                "& svg": { maxWidth: "100%", height: "auto" },
            }}
            dangerouslySetInnerHTML={{ __html: svg }}
        />
    );
};

const BlogPost: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [post, setPost] = useState<BlogPostMeta | null>(null);
    const [content, setContent] = useState<string>("");

    useEffect(() => {
        const fetchPost = async () => {
            const res = await fetch(`${process.env.PUBLIC_URL}/posts/blog-index.json`);
            const posts: BlogPostMeta[] = await res.json();
            const matched = posts.find((p) => p.slug === slug);

            if (matched) {
                setPost(matched);
                const contentRes = await fetch(`${process.env.PUBLIC_URL}/posts/${matched.file}`);
                const markdown = await contentRes.text();
                setContent(markdown);
            }
        };

        fetchPost();
    }, [slug]);

    if (!post) {
        return (
            <Container sx={{ mt: 4 }}>
                <Typography variant="h5">Blog post not found.</Typography>
            </Container>
        );
    }

    const markdownComponents = {
        img: ({ src, alt }: { src?: string; alt?: string }) => (
            <Box my={2}>
                <Zoom>
                    <img
                        src={src}
                        alt={alt}
                        style={{
                            maxWidth: "100%",
                            borderRadius: "8px",
                            cursor: "zoom-in",
                        }}
                    />
                </Zoom>
            </Box>
        ),
        // Intercept fenced code blocks. ```mermaid renders as a diagram;
        // everything else keeps the normal <pre> frame.
        pre: ({ children }: any) => {
            const child = Array.isArray(children) ? children[0] : children;
            const className: string = child?.props?.className || "";
            if (/language-mermaid/.test(className)) {
                const raw = child?.props?.children;
                const chart = String(Array.isArray(raw) ? raw.join("") : raw).trim();
                return <Mermaid chart={chart} />;
            }
            return <pre>{children}</pre>;
        },
    };

    return (
        <Container sx={{ mt: 6, maxWidth: "md" }}>
            <BreadcrumbsNav />
            <Box mb={1}>
                <Typography variant="h4" gutterBottom>{post.title}</Typography>
                <Box display="flex" alignItems="center" color="text.secondary" gap={1}>
                    <CalendarMonthIcon fontSize="small" />
                    <Typography variant="subtitle2">{post.date}</Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box
                sx={{
                    "& h1": { fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: "1.9rem", letterSpacing: "-0.02em", mt: 0, mb: 2 },
                    "& h2": { fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: "1.4rem", letterSpacing: "-0.01em", mt: 4, mb: 1.5 },
                    "& h3": { fontFamily: "'DM Mono', monospace", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "primary.main", mt: 3, mb: 1.5 },
                    "& p": { mb: 2, lineHeight: 1.8, color: "text.secondary" },
                    "& a": { color: "primary.main", textDecoration: "underline" },
                    "& ul, & ol": { pl: 3, mb: 2, color: "text.secondary" },
                    "& li": { mb: 0.75, lineHeight: 1.7 },
                    "& strong": { color: "text.primary", fontWeight: 500 },
                    "& hr": { border: "none", borderTop: "1px solid", borderColor: "divider", my: 4 },
                    // Code blocks and inline code
                    "& pre": {
                        bgcolor: "#0d1016",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        p: 2,
                        mb: 2.5,
                        overflowX: "auto",
                    },
                    "& pre code": {
                        fontFamily: "'DM Mono', monospace",
                        fontSize: "12.5px",
                        lineHeight: 1.6,
                        color: "#c9d1d9",
                        bgcolor: "transparent",
                        p: 0,
                        whiteSpace: "pre",
                    },
                    "& code": {
                        fontFamily: "'DM Mono', monospace",
                        fontSize: "0.85em",
                        bgcolor: "rgba(255,255,255,0.07)",
                        color: "#9ad1ff",
                        px: 0.6,
                        py: 0.2,
                        borderRadius: "4px",
                    },
                    // Tables
                    "& table": { borderCollapse: "collapse", width: "100%", mb: 2.5, fontSize: "13px", display: "block", overflowX: "auto" },
                    "& th, & td": { border: "1px solid", borderColor: "divider", p: "8px 12px", textAlign: "left", verticalAlign: "top" },
                    "& th": { bgcolor: "background.paper", fontFamily: "'DM Mono', monospace", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary", whiteSpace: "nowrap" },
                    "& td": { color: "text.secondary" },
                    // Blockquotes
                    "& blockquote": {
                        borderLeft: "3px solid",
                        borderColor: "primary.main",
                        pl: 2,
                        ml: 0,
                        my: 2,
                        color: "text.primary",
                        "& p": { color: "text.primary", mb: 0 },
                    },
                    fontSize: "1rem",
                }}
            >
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</ReactMarkdown>
            </Box>
        </Container>
    );
};

export default BlogPost;
